import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Warehouse, ShieldCheck, Heart, ClipboardList, Flame, Layers, Menu } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { NewLotsButton } from "@/components/NewLotsButton";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";

export function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: favCount } = useFavorites();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/"); setMenuOpen(false); };

  return (
    <header className="sticky top-0 z-40 bg-paper border-b border-ink">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
          <div className="bg-ink text-paper h-9 w-9 flex items-center justify-center">
            <Warehouse className="h-5 w-5" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-black text-lg tracking-tighter">STR SURPLUS</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-subtle">Operator Liquidation</div>
          </div>
        </Link>

        {/* Desktop nav (lg and up) */}
        <nav className="hidden lg:flex items-center gap-6 font-mono text-xs uppercase tracking-[0.15em]">
          <Link to="/" data-testid="nav-catalog" className="hover:text-link transition-colors">Catalog</Link>
          <Link to="/deals" data-testid="nav-deals" className="hover:text-danger transition-colors flex items-center gap-1"><Flame className="h-3.5 w-3.5" /> Deals</Link>
          <Link to="/lot-buys" data-testid="nav-lot-buys" className="hover:text-link transition-colors flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Lot Buys</Link>
          <Link to="/lots" data-testid="nav-lots" className="hover:text-link transition-colors">Lots</Link>
          <Link to="/favorites" data-testid="nav-favorites" className="hover:text-link transition-colors flex items-center gap-1">
            <Heart className="h-3.5 w-3.5" /> Saved{favCount > 0 ? ` (${favCount})` : ""}
          </Link>
          <a href="/#how" className="hover:text-link transition-colors">How It Works</a>
          {user && user.role === "admin" ? (
            <>
              <Link to="/admin" data-testid="nav-admin" className="hover:text-link transition-colors flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Admin
              </Link>
              <button data-testid="nav-logout" onClick={doLogout} className="hover:text-danger transition-colors uppercase">Logout</button>
            </>
          ) : user && user.role === "buyer" ? (
            <>
              <Link to="/orders" data-testid="nav-orders" className="hover:text-link transition-colors flex items-center gap-1">
                <ClipboardList className="h-3.5 w-3.5" /> My Orders
              </Link>
              <span data-testid="nav-user" className="text-subtle normal-case">Hi, {user.name}</span>
              <button data-testid="nav-logout" onClick={doLogout} className="hover:text-danger transition-colors uppercase">Logout</button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hover:text-link transition-colors">Sign In</Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <NewLotsButton className="hidden sm:flex" />
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 bg-ink text-paper px-4 h-10 font-mono text-xs uppercase tracking-[0.15em] hover:bg-link transition-colors"
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-discount text-ink h-5 min-w-5 px-1 flex items-center justify-center text-[11px] font-bold border border-ink">
                {count}
              </span>
            )}
          </button>

          {/* Hamburger menu (below lg) */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Open menu"
                className="lg:hidden flex items-center justify-center h-10 w-10 border border-ink hover:bg-ink hover:text-paper transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-paper border-l border-ink p-0">
              <div className="flex flex-col h-full">
                <div className="px-6 pt-6 pb-4 border-b border-ink">
                  <div className="font-heading font-black text-xl tracking-tighter">STR SURPLUS</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-subtle">Operator Liquidation</div>
                </div>
                <nav className="flex flex-col font-mono text-sm uppercase tracking-[0.15em] px-6 py-4 gap-1">
                  <SheetClose asChild><Link to="/" data-testid="m-nav-catalog" className="py-3 border-b border-surface2 hover:text-link transition-colors">Catalog</Link></SheetClose>
                  <SheetClose asChild><Link to="/deals" data-testid="m-nav-deals" className="py-3 border-b border-surface2 hover:text-danger transition-colors flex items-center gap-2"><Flame className="h-4 w-4" /> Deals</Link></SheetClose>
                  <SheetClose asChild><Link to="/lot-buys" data-testid="m-nav-lot-buys" className="py-3 border-b border-surface2 hover:text-link transition-colors flex items-center gap-2"><Layers className="h-4 w-4" /> Lot Buys</Link></SheetClose>
                  <SheetClose asChild><Link to="/lots" data-testid="m-nav-lots" className="py-3 border-b border-surface2 hover:text-link transition-colors">Lots</Link></SheetClose>
                  <SheetClose asChild><Link to="/favorites" data-testid="m-nav-favorites" className="py-3 border-b border-surface2 hover:text-link transition-colors flex items-center gap-2"><Heart className="h-4 w-4" /> Saved{favCount > 0 ? ` (${favCount})` : ""}</Link></SheetClose>
                  <SheetClose asChild><a href="/#how" data-testid="m-nav-how" className="py-3 border-b border-surface2 hover:text-link transition-colors">How It Works</a></SheetClose>

                  {user && user.role === "admin" ? (
                    <>
                      <SheetClose asChild><Link to="/admin" data-testid="m-nav-admin" className="py-3 border-b border-surface2 hover:text-link transition-colors flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Admin</Link></SheetClose>
                      <button data-testid="m-nav-logout" onClick={doLogout} className="py-3 text-left uppercase hover:text-danger transition-colors">Logout</button>
                    </>
                  ) : user && user.role === "buyer" ? (
                    <>
                      <SheetClose asChild><Link to="/orders" data-testid="m-nav-orders" className="py-3 border-b border-surface2 hover:text-link transition-colors flex items-center gap-2"><ClipboardList className="h-4 w-4" /> My Orders</Link></SheetClose>
                      <span data-testid="m-nav-user" className="py-3 text-subtle normal-case">Hi, {user.name}</span>
                      <button data-testid="m-nav-logout" onClick={doLogout} className="py-3 text-left uppercase hover:text-danger transition-colors">Logout</button>
                    </>
                  ) : (
                    <SheetClose asChild><Link to="/login" data-testid="m-nav-login" className="py-3 border-b border-surface2 hover:text-link transition-colors flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Sign In</Link></SheetClose>
                  )}
                </nav>
                <div className="mt-auto px-6 py-6 border-t border-ink sm:hidden">
                  <NewLotsButton className="w-full justify-center" />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
